import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import CSVImport from "./CSVImport";
import PerformanceAnalytics from "./PerformanceAnalytics";
import { useNotification } from "../NotificationContext";
import { formatDate } from "@/utils/timezone";
import {
  formatCurrency,
  formatPercent,
  getPerformanceClass,
  getPerformanceColor,
} from "@/utils/formatters";
import TradeHistory from "./TradeHistory";
import ColoredPercent from "../ColoredPercent";
import ColoredCurrency from "../ColoredCurrency";
import ThemeToggle from "../ThemeToggle";
import GlobalDropZone from "../GlobalDropZone";
import OldIntelLibrary from "../OldIntelLibrary";
import IntelSidebar from "../IntelSidebar";
import { SyncStatusIndicator } from "../plaid";
import api from "@/api";

const OldAccountView = ({ accounts, onRefresh }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("weekly");
  const [showAddHolding, setShowAddHolding] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [activeTab, setActiveTab] = useState("holdings");
  const [analyticsMode, setAnalyticsMode] = useState("periods");
  const [chartMetric, setChartMetric] = useState("account_balance");
  const [currentPerformance, setCurrentPerformance] = useState(null);
  const [accountCurrent, setAccountCurrent] = useState(null);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const refreshingRef = useRef(false);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTicker, setSidebarTicker] = useState(null);
  const [intel, setIntel] = useState([]);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const { showSuccess, showError, showWarning } = useNotification();

  const fetchHoldings = async () => {
    try {
      setLoading(true);

      // Fetch current account status and performance in parallel
      const [currentResponse, performanceResponse] = await Promise.all([
        api.get(`/accounts/${id}/current`),
        api.get(`/accounts/${id}/current-performance`),
      ]);

      setHoldings(currentResponse.data.holdings);
      setAccountCurrent(currentResponse.data);
      setCurrentPerformance(performanceResponse.data);

      // Performance data for new schema (not implemented yet)
      setPerformance(null);
    } catch (error) {
      console.error("Error fetching account data:", error);
      // Set to null on error to avoid stale data
      setCurrentPerformance(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntel = async () => {
    try {
      const response = await api.get(`/intel`);
      setIntel(response.data.items || response.data || []);
    } catch (error) {
      console.error("Error fetching intel:", error);
      setIntel([]);
    }
  };

  const handleIntelClick = (ticker) => {
    setSidebarTicker(ticker);
    setSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setSidebarTicker(null);
  };

  useEffect(() => {
    if (id) {
      fetchHoldings();
      fetchIntel();
    }
  }, [id, selectedPeriod]);

  // Auto-refresh countdown timer
  useEffect(() => {
    if (!accountCurrent?.account?.last_prices_refreshed) {
      setCountdown(null);
      return;
    }

    // Don't auto-refresh if there's a network error
    if (hasNetworkError) {
      setCountdown(null);
      return;
    }

    // Don't auto-refresh while CSV import modal is open
    if (showCSVImport) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const lastRefresh = new Date(
        accountCurrent.account.last_prices_refreshed
      );
      const nextRefreshTime = new Date(lastRefresh.getTime() + 5 * 60 * 1000); // 5 minutes
      const secondsRemaining = Math.floor((nextRefreshTime - now) / 1000);

      setCountdown(secondsRemaining);

      // If countdown expired, trigger refresh (ref prevents duplicates)
      if (secondsRemaining <= 0 && !hasNetworkError && !showCSVImport) {
        refreshPrices();
      }
    };

    // Update immediately
    updateCountdown();

    // Then update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [
    accountCurrent?.account?.last_prices_refreshed,
    hasNetworkError,
    showCSVImport,
  ]);

  // Poll for sync status
  useEffect(() => {
    if (!id) return;

    const checkSyncStatus = async () => {
      try {
        const response = await api.get(`/accounts/${id}`);
        const accountData = response.data;

        const isSyncing = accountData.sync_in_progress || false;
        const status = accountData.sync_status || null;

        setSyncInProgress(isSyncing);
        setSyncStatus(status);

        // If sync just completed, refresh the account data
        if (!isSyncing && syncInProgress) {
          console.log("Sync completed, refreshing account data...");
          fetchHoldings();
        }
      } catch (error) {
        console.error("Error checking sync status:", error);
      }
    };

    // Check immediately
    checkSyncStatus();

    // Poll every 2 seconds while syncing, every 10 seconds otherwise
    const pollInterval = syncInProgress ? 2000 : 10000;
    const interval = setInterval(checkSyncStatus, pollInterval);

    return () => clearInterval(interval);
  }, [id, syncInProgress]);

  const formatCountdown = (seconds) => {
    if (seconds === null) return null;
    if (seconds <= 0) return "Refreshing...";

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (minutes > 0) {
      return `Next refresh in ${minutes}m ${secs}s`;
    }
    return `Next refresh in ${secs}s`;
  };

  const refreshPrices = async () => {
    // Prevent duplicate calls
    if (refreshingRef.current) {
      return;
    }

    try {
      refreshingRef.current = true;
      setRefreshingPrices(true);

      const response = await api.post(`/accounts/${id}/refresh`);

      // Update state with fresh data from response
      setHoldings(response.data.holdings);
      setAccountCurrent(response.data);
      onRefresh(); // Refresh parent component's account list

      // Clear any previous network errors on success
      setHasNetworkError(false);

      // Show success notification
      showSuccess("Prices updated successfully");
    } catch (error) {
      // Determine error type
      const isNetworkError =
        !error.response || // No response = network error
        error.code === "ECONNABORTED" || // Timeout
        error.code === "ERR_NETWORK" || // Network error
        error.message === "Network Error"; // Generic network error

      if (isNetworkError) {
        // Network error (no wifi, timeout, etc.)
        console.error("Network error refreshing prices:", error);
        setHasNetworkError(true);
        showError(
          "Cannot connect to server. Please check your internet connection. Auto-refresh has been paused.",
          8000
        );
      } else if (error.response?.status === 429) {
        // Rate limited
        showWarning(error.response.data.detail, 5000);
      } else {
        // Other server errors
        console.error("Error refreshing prices:", error);
        showError(
          `Failed to refresh prices: ${
            error.response?.data?.detail || error.message
          }`,
          5000
        );
      }
    } finally {
      refreshingRef.current = false;
      setRefreshingPrices(false);
    }
  };

  const deleteHolding = async (holdingId) => {
    try {
      // Delete holding not implemented for new schema yet
      console.log(
        "Delete holding not implemented for new schema yet:",
        holdingId
      );
      alert("Delete holding not implemented for new schema yet");
      return;
    } catch (error) {
      console.error("Error deleting holding:", error);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading account data...
      </div>
    );
  }

  if (!accountCurrent) {
    return <div className="loading">Account not found</div>;
  }
  const account = accountCurrent.account;

  return (
    <div
      className="dashboard"
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div className="dashboard-header" style={{ flexShrink: 0 }}>
        <div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/old")}
            style={{ marginBottom: "1rem" }}
            data-testid="back-button"
          >
            ← Back to Dashboard
          </button>
          <h1 className="dashboard-title" data-testid="account-title">
            {account.name}
          </h1>
          <p className="dashboard-subtitle">
            {holdings?.length || 0} holdings
            {hasNetworkError ? (
              <>
                {" "}
                •{" "}
                <span style={{ color: "#f56565" }}>
                  Network error - Auto-refresh paused
                </span>
              </>
            ) : formatCountdown(countdown) ? (
              <> • {formatCountdown(countdown)}</>
            ) : accountCurrent &&
              !accountCurrent.account?.last_prices_refreshed ? (
              <> • Click refresh to update account</>
            ) : null}
          </p>
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <ThemeToggle />
          <button
            className="btn btn-secondary"
            onClick={refreshPrices}
            disabled={refreshingPrices}
            data-testid="refresh-holdings-button"
            style={{
              opacity: refreshingPrices ? 0.6 : 1,
              cursor: refreshingPrices ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {refreshingPrices ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Refreshing...
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {hasNetworkError ? "Retry Connection" : "Refresh"}
              </>
            )}
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => setShowCSVImport(true)}
            data-testid="import-csv-button"
          >
            📊 Import CSV
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddHolding(true)}
            data-testid="add-holding-button"
          >
            ➕ Add Holding
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 2rem 2rem 2rem" }}>
        {showAddHolding && (
          <AddHoldingForm
            accountId={id}
            onClose={() => setShowAddHolding(false)}
            onHoldingAdded={() => {
              fetchHoldings();
              onRefresh();
            }}
          />
        )}

        {showCSVImport && (
          <CSVImport
            accountId={id}
            onClose={() => {
              setShowCSVImport(false);
              fetchHoldings();
              onRefresh();
            }}
            onImportComplete={() => {
              // Don't refresh here - it unmounts the modal before showing results
              // Data will refresh when user closes the modal
            }}
          />
        )}

        {/* Sync Status Indicator */}
        <SyncStatusIndicator
          syncing={syncInProgress}
          status={syncStatus}
          className="mb-4"
        />

        {/* Account Summary */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
            marginBottom: "2rem",
          }}
        >
          <div
            className="account-card"
            onClick={() => {
              setActiveTab("analytics");
              setAnalyticsMode("chart");
              setChartMetric("account_balance");
            }}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-label">Account Balance</div>
            <div
              className="stat-value"
              style={{ fontSize: "1.5rem" }}
              data-testid="total-value"
            >
              {formatCurrency(accountCurrent?.total_value || 0)}
            </div>
            <div
              className="text-muted"
              style={{
                fontSize: "0.75rem",
                marginTop: "0.25rem",
              }}
            >
              Stocks: {formatCurrency(accountCurrent?.portfolio_value || 0)}
              <br />
              Cash: {formatCurrency(accountCurrent?.cash_balance || 0)}
            </div>
          </div>
          <div
            className="account-card"
            onClick={() => {
              setActiveTab("analytics");
              setAnalyticsMode("chart");
              setChartMetric("stock_value");
            }}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-label">Stock Value</div>
            <div
              className="stat-value"
              style={{ fontSize: "1.5rem" }}
              data-testid="stock-value"
            >
              {formatCurrency(accountCurrent?.portfolio_value || 0)}
            </div>
            <div style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
              <span className="text-muted">Unrealized P&L: </span>
              <ColoredCurrency
                value={accountCurrent?.unrealized_gain_loss || 0}
              />
              {accountCurrent?.unrealized_gain_loss !== 0 && (
                <span>
                  {" "}
                  (
                  <ColoredPercent
                    value={
                      (accountCurrent?.unrealized_gain_loss /
                        accountCurrent?.cost_basis) *
                      100
                    }
                  />
                  )
                </span>
              )}
            </div>
            <div
              className="text-muted"
              style={{
                fontSize: "0.75rem",
                marginTop: "0.25rem",
              }}
            >
              Cost Basis: {formatCurrency(accountCurrent?.cost_basis || 0)}
            </div>
          </div>
          <div
            className="account-card"
            onClick={() => {
              setActiveTab("analytics");
              setAnalyticsMode("chart");
              setChartMetric("cash_pending");
            }}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-label">Cash + Pending</div>
            <div
              className="stat-value"
              style={{ fontSize: "1.5rem" }}
              data-testid="cash-balance"
            >
              {formatCurrency(accountCurrent?.cash_balance || 0)}
            </div>
            <div
              className="text-muted"
              style={{
                fontSize: "0.875rem",
                marginTop: "0.25rem",
              }}
            >
              {formatPercent(
                ((accountCurrent?.cash_balance || 0) /
                  (accountCurrent?.total_value || 1)) *
                  100
              )}{" "}
              of account
            </div>
            {/* Pending value not available in new schema */}
          </div>
          <div
            className="account-card"
            onClick={() => {
              setActiveTab("performance");
              setSelectedPeriod("daily"); // Set to daily when clicking
            }}
            style={{
              cursor: "pointer",
              transition: "all 0.2s ease",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.05)";
            }}
            data-testid="todays-change-widget"
          >
            <div className="stat-label">Today's P&L</div>
            <div
              className={`stat-value ${getPerformanceClass(
                currentPerformance?.daily?.total_pnl || 0
              )}`}
              style={{
                fontSize: "1.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
              data-testid="todays-performance"
            >
              <span>
                {formatCurrency(currentPerformance?.daily?.total_pnl || 0)}
              </span>
              <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                <ColoredPercent
                  value={currentPerformance?.daily?.pnl_percent || 0}
                />
              </span>
            </div>

            {/* P&L Breakdown */}
            {currentPerformance?.daily && (
              <div
                style={{
                  fontSize: "0.75rem",
                  lineHeight: "1.4",
                  marginTop: "0.5rem",
                }}
              >
                <div style={{ display: "flex" }}>
                  <span
                    style={{
                      width: "50%",
                      textAlign: "right",
                      paddingRight: "0.25rem",
                    }}
                  >
                    Realized:
                  </span>
                  <span style={{ width: "50%", textAlign: "left" }}>
                    <ColoredCurrency
                      value={currentPerformance.daily.realized_pnl || 0}
                    />
                  </span>
                </div>
                <div style={{ display: "flex" }}>
                  <span
                    style={{
                      width: "50%",
                      textAlign: "right",
                      paddingRight: "0.25rem",
                    }}
                  >
                    Unrealized:
                  </span>
                  <span style={{ width: "50%", textAlign: "left" }}>
                    <ColoredCurrency
                      value={currentPerformance.daily.unrealized_pnl || 0}
                    />
                  </span>
                </div>
              </div>
            )}

            {/* Week/Month sub-metrics */}
            {currentPerformance && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0.5rem",
                  marginTop: "1rem",
                  fontSize: "0.75rem",
                }}
              >
                <div
                  className="stat-card-subtle"
                  style={{ textAlign: "center" }}
                >
                  <div
                    className="text-muted"
                    style={{ marginBottom: "0.25rem" }}
                  >
                    This Week
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                    }}
                  >
                    <ColoredCurrency
                      value={currentPerformance.weekly?.total_pnl || 0}
                    />
                  </div>
                </div>
                <div
                  className="stat-card-subtle"
                  style={{ textAlign: "center" }}
                >
                  <div
                    className="text-muted"
                    style={{ marginBottom: "0.25rem" }}
                  >
                    This Month
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                    }}
                  >
                    <ColoredCurrency
                      value={currentPerformance.monthly?.total_pnl || 0}
                    />
                  </div>
                </div>
              </div>
            )}

            <div
              className="text-muted"
              style={{
                fontSize: "0.75rem",
                textAlign: "center",
                marginTop: "0.5rem",
              }}
            >
              Click to view daily analytics
            </div>
          </div>
        </div>

        {/* Tabbed Interface */}
        <div
          className="account-card"
          style={{ padding: 0, overflow: "hidden" }}
        >
          {/* Tab Navigation */}
          <div
            className="border-subtle"
            style={{ display: "flex", borderBottom: "1px solid" }}
          >
            <button
              className={`tab-button ${
                activeTab === "holdings" ? "active" : ""
              }`}
              onClick={() => setActiveTab("holdings")}
              data-testid="holdings-tab"
              style={{
                padding: "1rem 2rem",
                fontWeight: activeTab === "holdings" ? "600" : "500",
              }}
            >
              📊 Account Holdings
            </button>
            <button
              className={`tab-button ${
                activeTab === "performance" ? "active" : ""
              }`}
              onClick={() => setActiveTab("performance")}
              data-testid="performance-tab"
              style={{
                padding: "1rem 2rem",
                fontWeight: activeTab === "performance" ? "600" : "500",
              }}
            >
              📈 Performance Analytics
            </button>
            <button
              className={`tab-button ${
                activeTab === "history" ? "active" : ""
              }`}
              onClick={() => setActiveTab("history")}
              data-testid="history-tab"
              style={{
                padding: "1rem 2rem",
                fontWeight: activeTab === "history" ? "600" : "500",
              }}
            >
              📜 Trade History
            </button>
            <button
              className={`tab-button ${activeTab === "intel" ? "active" : ""}`}
              onClick={() => setActiveTab("intel")}
              data-testid="intel-tab"
              style={{
                padding: "1rem 2rem",
                fontWeight: activeTab === "intel" ? "600" : "500",
              }}
            >
              📸 Intel Library
            </button>
          </div>

          {/* Tab Content */}
          <div style={{ minHeight: "400px" }}>
            {activeTab === "holdings" && (
              <div>
                <div className="section-content">
                  <h3 className="section-title">Current Holdings</h3>
                  {/* <p
                  className="section-description"
                  style={{ marginBottom: "1rem" }}
                >
                  Real-time view of your stock positions with current values and
                  P&L
                </p> */}
                </div>

                {!holdings || holdings.length === 0 ? (
                  <div className="empty-state" data-testid="no-holdings">
                    <h3>No holdings yet</h3>
                    <p>Add your first stock position to start tracking</p>
                  </div>
                ) : (
                  <HoldingsTable
                    holdings={holdings}
                    onDeleteHolding={deleteHolding}
                    accountId={id}
                  />
                )}
              </div>
            )}

            {activeTab === "performance" && (
              <PerformanceAnalytics
                accountId={id}
                account={account}
                holdings={holdings}
                initialPeriod={selectedPeriod}
                autoSelectToday={selectedPeriod === "daily"}
                initialMode={analyticsMode}
                chartMetric={chartMetric}
              />
            )}

            {activeTab === "history" && (
              <TradeHistory
                accountId={id}
                showFilters={true}
                showPagination={true}
                title="Trade History"
                emptyMessage="No transactions found"
                onIntelClick={handleIntelClick}
              />
            )}

            {activeTab === "intel" && (
              <OldIntelLibrary accountId={id} onIntelClick={handleIntelClick} />
            )}
          </div>
        </div>
      </div>

      {/* Global Drop Zone - allows dropping screenshots/CSV anywhere */}
      <GlobalDropZone
        accounts={accounts}
        currentAccountId={id}
        onUploadComplete={(upload) => {
          // Refresh data after upload completes
          fetchHoldings();
          fetchIntel();
          onRefresh();
        }}
      />

      {/* Intel Sidebar - slide-in panel for viewing ticker intel */}
      <IntelSidebar
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        ticker={sidebarTicker}
        intel={intel}
        accountId={id}
        onIntelDeleted={(intelId) => {
          // Remove from local state
          setIntel((prev) => prev.filter((item) => item.id !== intelId));
        }}
      />
    </div>
  );
};

const HoldingsTable = ({ holdings, onDeleteHolding, accountId }) => {
  const [sortField, setSortField] = useState("unrealized_gain_loss");
  const [sortDirection, setSortDirection] = useState("desc");

  const sortedHoldings = [...holdings].sort((a, b) => {
    let aVal = a[sortField] || 0;
    let bVal = b[sortField] || 0;

    if (sortDirection === "asc") {
      return aVal - bVal;
    }
    return bVal - aVal;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="performance-table data-table">
        <thead>
          <tr>
            <th onClick={() => handleSort("ticker")}>
              Ticker {getSortIndicator("ticker")}
            </th>
            {/* <th style={headerStyle} onClick={() => handleSort("company_name")}>
              Company {getSortIndicator("company_name")}
            </th>
            <th style={headerStyle} onClick={() => handleSort("industry")}>
              Industry {getSortIndicator("industry")}
            </th> */}
            <th onClick={() => handleSort("shares")}>
              Qty {getSortIndicator("shares")}
            </th>
            <th onClick={() => handleSort("avg_cost_basis")}>
              Entry {getSortIndicator("avg_cost_basis")}
            </th>
            <th onClick={() => handleSort("current_price")}>
              Current {getSortIndicator("current_price")}
            </th>
            <th onClick={() => handleSort("market_value")}>
              Value {getSortIndicator("market_value")}
            </th>
            <th onClick={() => handleSort("unrealized_gain_loss")}>
              P&L $ {getSortIndicator("unrealized_gain_loss")}
            </th>
            <th onClick={() => handleSort("unrealized_gain_loss_percent")}>
              P&L % {getSortIndicator("unrealized_gain_loss_percent")}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedHoldings.map((holding) => (
            <tr key={holding.id}>
              <td>
                <Link
                  to={`/old/account/${accountId}/ticker/${holding.ticker}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <strong
                    data-testid={`ticker-${holding.ticker}`}
                    style={{ cursor: "pointer" }}
                  >
                    {holding.ticker}
                  </strong>
                </Link>
              </td>
              {/* <td style={cellStyle} data-testid={`company-${holding.ticker}`}>
                -
              </td>
              <td style={cellStyle} data-testid={`industry-${holding.ticker}`}>
                -
              </td> */}
              <td data-testid={`quantity-${holding.ticker}`}>
                {holding.shares}
              </td>
              <td data-testid={`entry-price-${holding.ticker}`}>
                {formatCurrency(holding.avg_cost_basis)}
              </td>
              <td data-testid={`current-price-${holding.ticker}`}>
                {formatCurrency(holding.current_price)}
              </td>
              <td data-testid={`current-value-${holding.ticker}`}>
                {formatCurrency(holding.market_value)}
              </td>
              <td data-testid={`pnl-total-${holding.ticker}`}>
                <ColoredCurrency value={holding.unrealized_gain_loss} />
              </td>
              <td data-testid={`pnl-percent-${holding.ticker}`}>
                <ColoredPercent
                  value={
                    (holding.unrealized_gain_loss /
                      (holding.shares * holding.avg_cost_basis)) *
                    100
                  }
                />
              </td>
              <td>
                <button
                  className="btn btn-danger"
                  style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                  onClick={() => onDeleteHolding(holding.id)}
                  data-testid={`delete-holding-${holding.ticker}`}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AddHoldingForm = ({ accountId, onClose, onHoldingAdded }) => {
  const [formData, setFormData] = useState({
    ticker: "",
    company_name: "",
    industry: "",
    quantity: "",
    entry_price: "",
    entry_date: new Date().toISOString().split("T")[0],
  });
  const [loading, setLoading] = useState(false);

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.ticker ||
      !formData.quantity ||
      !formData.entry_price ||
      !formData.entry_date
    ) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      const holdingData = {
        account_id: accountId,
        ticker: formData.ticker.toUpperCase(),
        company_name: formData.company_name,
        industry: formData.industry,
        quantity: parseFloat(formData.quantity),
        entry_price: parseFloat(formData.entry_price),
        entry_date: new Date(formData.entry_date).toISOString(),
      };

      await api.post("/holdings", holdingData);
      onHoldingAdded();
      onClose();
    } catch (error) {
      console.error("Error creating holding:", error);
      alert("Error creating holding");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="form-container"
        style={{ maxWidth: "600px" }}
        data-testid="add-holding-form"
      >
        <h2 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
          Add New Holding
        </h2>

        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className="form-group">
              <label className="form-label">Ticker *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., AAPL"
                value={formData.ticker}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    ticker: e.target.value.toUpperCase(),
                  })
                }
                data-testid="ticker-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Entry Date *</label>
              <input
                type="date"
                className="form-input"
                value={formData.entry_date}
                onChange={(e) =>
                  setFormData({ ...formData, entry_date: e.target.value })
                }
                data-testid="entry-date-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Company Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Apple Inc."
              value={formData.company_name}
              onChange={(e) =>
                setFormData({ ...formData, company_name: e.target.value })
              }
              data-testid="company-name-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Industry</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Technology"
              value={formData.industry}
              onChange={(e) =>
                setFormData({ ...formData, industry: e.target.value })
              }
              data-testid="industry-input"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className="form-group">
              <label className="form-label">Quantity *</label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                placeholder="100"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: e.target.value })
                }
                data-testid="quantity-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Entry Price *</label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                placeholder="150.00"
                value={formData.entry_price}
                onChange={(e) =>
                  setFormData({ ...formData, entry_price: e.target.value })
                }
                data-testid="entry-price-input"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
              data-testid="cancel-holding-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              data-testid="create-holding-button"
            >
              {loading ? "Adding..." : "Add Holding"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OldAccountView;
